import { useState } from "react";

import { ApiError } from "@incorposano/api-client";

import { Button } from "@/design-system";
import { useLoginMutation } from "@/api/queries";
import { useScreenTitle } from "@/lib/use-screen-title";

import { AuthLayout } from "./AuthLayout";
import { Field } from "./Field";

export interface LoginProps {
  onBack: () => void;
  onSignup: () => void;
  onForgotPassword: () => void;
  onLoggedIn: () => void;
}

export function Login({
  onBack,
  onSignup,
  onForgotPassword,
  onLoggedIn,
}: LoginProps) {
  useScreenTitle("Accedi");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = useLoginMutation();

  const formError = login.isError ? mapLoginError(login.error) : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    login.mutate(
      { email: email.trim().toLowerCase(), password },
      { onSuccess: () => onLoggedIn() },
    );
  }

  return (
    <AuthLayout
      title="Bentornato"
      subtitle="Accedi per ritrovare la tua conversazione e il tuo piano."
      onBack={onBack}
      footer={
        <span>
          Non hai un account?{" "}
          <button
            type="button"
            onClick={onSignup}
            className="border-b border-dotted border-ink-faint bg-transparent border-x-0 border-t-0 cursor-pointer text-ink"
          >
            Registrati
          </button>
        </span>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="nome@esempio.it"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div className="flex justify-end -mt-2 mb-4">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-[13px] text-ink-muted border-b border-dotted border-ink-faint bg-transparent border-x-0 border-t-0 cursor-pointer"
          >
            Hai dimenticato la password?
          </button>
        </div>
        {formError && (
          <p className="text-[13px] text-brick-danger mb-3">{formError}</p>
        )}
        <Button type="submit" variant="primary" full disabled={login.isPending}>
          {login.isPending ? "Accesso…" : "Accedi"}
        </Button>
      </form>
    </AuthLayout>
  );
}

// ICSP-25: differentiate at the status level via ApiError.status rather
// than fuzzy-matching the error message text. Two structured cases (403
// email-not-verified, 401 invalid-credentials), plus 422 for client-side
// malformed-email validation, plus a generic fallback. The 401 message
// stays intentionally vague ("Email o password non corretti.") so we
// don't help an attacker enumerate which field is wrong.
export function mapLoginError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403 || err.body.includes("email_not_verified"))
      return "Devi prima confermare la tua email. Controlla la posta.";
    if (err.status === 401 || err.body.includes("invalid_credentials"))
      return "Email o password non corretti.";
    if (err.status === 422)
      return "Inserisci un'email valida (es. nome@esempio.it).";
  } else {
    // Older code paths still throw bare Errors with HTTP codes in the
    // message — keep the string-match branches as a fallback so a
    // staged rollout of the typed-client doesn't regress the UX.
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("403") || msg.includes("email_not_verified"))
      return "Devi prima confermare la tua email. Controlla la posta.";
    if (msg.includes("401") || msg.includes("invalid_credentials"))
      return "Email o password non corretti.";
    if (msg.includes("422"))
      return "Inserisci un'email valida (es. nome@esempio.it).";
  }
  return "Qualcosa è andato storto. Riprova fra qualche istante.";
}
